<template>
  <div class="mt-4">
    <div class="card p-3">
      <h5 class="mb-3 section-title card-section-title">Informe de Extrusión</h5>

      <!-- Filtros: buscador izquierda, fechas derecha (mismo tamaño y posición en todas las tarjetas) -->
      <div class="card-filters-row d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
        <div class="card-filters-buscar">
          <label for="buscar_extrusion" class="form-label">Buscar</label>
          <input
            id="buscar_extrusion"
            type="text"
            class="form-control"
            v-model="buscar"
            placeholder="Buscar por modelo o máquina..."
          />
        </div>
        <div class="card-filters-fechas d-flex flex-wrap align-items-end gap-5">
          <div class="card-filters-fecha">
            <label for="fecha_inicio" class="form-label">Fecha inicio</label>
            <input id="fecha_inicio" type="date" class="form-control" v-model="fecha_inicio" />
          </div>
          <div class="card-filters-fecha">
            <label for="fecha_fin" class="form-label">Fecha fin</label>
            <input id="fecha_fin" type="date" class="form-control" v-model="fecha_fin" />
          </div>
        </div>
      </div>

      <div class="row">
        <!-- Izquierda: tabla -->
        <div class="col-lg-6">
          <div class="table-responsive">
            <table class="table table-sm text-center table-informe-extrusion">
              <thead>
                <tr>
                  <th class="text-center">ID</th>
                  <th class="text-center">Modelo</th>
                  <th class="text-center">Máquina</th>
                  <th class="text-center">Programado</th>
                  <th class="text-center">Perdido</th>
                  <th class="text-center">Efectivo</th>
                  <th class="text-center">Cant. Extr.</th>
                  <th class="text-center">Consumo Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in tablaExtrusionRows" :key="item ? item.id : `empty-${idx}`" :class="{ 'fila-vacia': !item }">
                  <td class="text-center">{{ item ? item.id : '—' }}</td>
                  <td class="text-center">{{ item ? (item.modelo || '—') : '—' }}</td>
                  <td class="text-center">{{ item ? (item.extrusion?.maquina || '—') : '—' }}</td>
                  <td class="text-center">{{ item ? item.extrusion?.tiempo_programado : '—' }}</td>
                  <td class="text-center">{{ item ? item.extrusion?.tiempo_perdido : '—' }}</td>
                  <td class="text-center">{{ item ? item.extrusion?.tiempo_efectivo : '—' }}</td>
                  <td class="text-center">{{ item ? item.extrusion?.cantidad_extruida : '—' }}</td>
                  <td class="text-center">{{ item ? item.extrusion?.consumo_total : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="extrusion-pagination d-flex justify-content-center align-items-center mt-2 gap-2">
            <button
              type="button"
              class="extrusion-pagination-btn"
              @click="prevPageExtrusion"
              :disabled="currentPage === 1"
            >
              &lt;&lt;
            </button>
            <span class="extrusion-pagination-text">{{ currentPage }}/{{ totalPages }}</span>
            <button
              type="button"
              class="extrusion-pagination-btn"
              @click="nextPageExtrusion"
              :disabled="currentPage >= totalPages"
            >
              &gt;&gt;
            </button>
          </div>
        </div>

        <!-- Derecha: total arriba (centrado), luego los dos gráficos -->
        <div class="col-lg-6 text-center">
          <p class="mb-3 extrusion-total-text">
            <strong>Total de Producciones:</strong> {{ buscar.trim() ? produccionesFiltradas.length : resumen.total_registros }}
          </p>
          <div class="extrusion-chart-wrap mb-4">
            <canvas ref="extrusionChartCantidad"></canvas>
          </div>
          <div class="extrusion-chart-wrap mb-4">
            <canvas ref="extrusionChartConsumo"></canvas>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default {
  name: "InformeExtrusion",
  data() {
    const hoy = new Date();
    const hace15 = new Date();
    hace15.setDate(hoy.getDate() - 15);

    const formatDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    return {
      fecha_inicio: formatDate(hace15),
      fecha_fin: formatDate(hoy),
      buscar: '',
      resumen: { total_registros: 0, rango: { desde: null, hasta: null } },
      producciones: [],
      totales: [],
      chartInstanceCantidad: null,
      chartInstanceConsumo: null,
      currentPage: 1,
      perPage: 11,
      debounceTimer: null,
      fetchSeq: 0,
    };
  },
  computed: {
    produccionesFiltradas() {
      return this.producciones;
    },
    totalPages() {
      const total = this.produccionesFiltradas.length;
      return Math.max(1, Math.ceil(total / this.perPage));
    },
    tablaExtrusionRows() {
      const data = this.produccionesFiltradas;
      const start = (this.currentPage - 1) * this.perPage;
      const pageData = data.slice(start, start + this.perPage);
      const rows = [...pageData];
      while (rows.length < this.perPage) rows.push(null);
      return rows;
    },
    totalesParaGrafico() {
      const byModel = {};
      const source = Array.isArray(this.producciones) ? this.producciones : [];

      source.forEach((p) => {
        const key = p.inventory_id ?? p.modelo ?? `sin-modelo-${p.id}`;
        if (!byModel[key]) {
          byModel[key] = {
            inventory_id: p.inventory_id,
            modelo: p.modelo || `#${p.inventory_id ?? p.id}`,
            total_extrusion: 0,
            total_consumo: 0,
          };
        }
        byModel[key].total_extrusion += Number(p.extrusion?.cantidad_extruida) || 0;
        byModel[key].total_consumo += Number(p.extrusion?.consumo_total) || 0;
      });

      return Object.values(byModel);
    },
    totalExtrusionTabla() {
      return this.producciones.reduce(
        (acc, p) => acc + (Number(p?.extrusion?.cantidad_extruida) || 0),
        0
      );
    },
    totalConsumoTabla() {
      return this.producciones.reduce(
        (acc, p) => acc + (Number(p?.extrusion?.consumo_total) || 0),
        0
      );
    },
  },
  watch: {
    fecha_inicio() {
      this.fetchInforme();
    },
    fecha_fin() {
      this.fetchInforme();
    },
    buscar() {
      this.currentPage = 1;
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      const q = (this.buscar || "").trim();
      if (q === "") {
        this.fetchInforme();
        return;
      }
      this.debounceTimer = setTimeout(() => this.fetchInforme(), 400);
    },
  },
  methods: {
    prevPageExtrusion() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPageExtrusion() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    async fetchInforme() {
      try {
        const reqId = ++this.fetchSeq;
        const params = {
          fecha_inicio: this.fecha_inicio,
          fecha_fin: this.fecha_fin,
        };
        const q = (this.buscar || "").trim();
        if (q) params.buscar = q;

        const res = await this.$store.dispatch("analitycs/getInformeExtrusion", params);
        const data = res?.data?.data ?? res?.data ?? {};
        if (reqId !== this.fetchSeq) return;

        this.resumen = {
          total_registros: data.total_registros || 0,
          rango: data.rango || {},
        };
        this.producciones = data.producciones || [];
        this.totales = data.totales || [];
        this.currentPage = 1;

        this.$nextTick(() => this.renderChart());
      } catch (error) {
        console.error("Error cargando informe de extrusión:", error);
      }
    },

    renderChart() {
      if (this.chartInstanceCantidad) {
        this.chartInstanceCantidad.destroy();
        this.chartInstanceCantidad = null;
      }
      if (this.chartInstanceConsumo) {
        this.chartInstanceConsumo.destroy();
        this.chartInstanceConsumo = null;
      }

      const totales = this.totalesParaGrafico;
      const dataExtrusion = totales.map((t) => t.total_extrusion);
      const dataConsumo = totales.map((t) => t.total_consumo);
      const totalConsumo = dataConsumo.length ? dataConsumo.reduce((a, b) => a + b, 0) : 0;
      const totalExtrusion = dataExtrusion.length ? dataExtrusion.reduce((a, b) => a + b, 0) : 0;
      const extrusionValue = this.totalExtrusionTabla || totalExtrusion;
      const consumoValue = this.totalConsumoTabla || totalConsumo;

      const opts = {
        responsive: true,
        maintainAspectRatio: true,
        animation: false,
        layout: { padding: { top: 8, right: 8, bottom: 8, left: 8 } },
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: { usePointStyle: true, font: { size: 12 }, color: "#1f2d5a", padding: 12 },
          },
          tooltip: {
            backgroundColor: "#1f2d5a",
            padding: 10,
            titleFont: { size: 13 },
            bodyFont: { size: 13 },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 10 }, color: "#1f2d5a", maxRotation: 45 },
          },
          y: {
            beginAtZero: true,
            grid: { color: "rgba(0,0,0,0.06)" },
            ticks: { font: { size: 11 }, color: "#6c757d" },
          },
        },
      };

      const ctxConsumo = this.$refs.extrusionChartConsumo;
      if (ctxConsumo) {
        // Gráfico horizontal: eje X = valores (Consumo Total), sin nombre de producto
        this.chartInstanceConsumo = new Chart(ctxConsumo, {
          type: "bar",
          data: {
            labels: ["Consumo total"],
            datasets: [
              {
                label: "Consumo Total",
                data: [consumoValue],
                backgroundColor: "rgba(246, 165, 0, 0.9)",
                borderRadius: 4,
                barThickness: 14,
              },
            ],
          },
          options: {
            ...opts,
            indexAxis: "y",
            scales: {
              x: {
                beginAtZero: true,
                max: consumoValue > 0 ? consumoValue : 1,
                grace: 0,
                grid: { color: "rgba(0,0,0,0.04)" },
                ticks: { font: { size: 11 }, color: "#6c757d" },
                title: { display: false },
              },
              y: {
                grid: { display: false },
                ticks: { display: false },
              },
            },
          },
        });
      }

      const ctxCantidad = this.$refs.extrusionChartCantidad;
      if (ctxCantidad) {
        // Mismo criterio: eje X = valores (Cantidad de extrusión), sin nombre de producto
        this.chartInstanceCantidad = new Chart(ctxCantidad, {
          type: "bar",
          data: {
            labels: ["Cantidad de extrusión"],
            datasets: [
              {
                label: "Cantidad de extrusión",
                data: [extrusionValue],
                backgroundColor: "rgba(31, 45, 90, 0.85)",
                borderRadius: 4,
                barThickness: 14,
              },
            ],
          },
          options: {
            ...opts,
            indexAxis: "y",
            scales: {
              x: {
                beginAtZero: true,
                max: extrusionValue > 0 ? extrusionValue : 1,
                grace: 0,
                grid: { color: "rgba(0,0,0,0.04)" },
                ticks: { font: { size: 11 }, color: "#6c757d" },
                title: { display: false },
              },
              y: {
                grid: { display: false },
                ticks: { display: false },
              },
            },
          },
        });
      }
    },
  },
  mounted() {
    this.fetchInforme();
  },
  beforeUnmount() {
    if (this.chartInstanceCantidad) {
      this.chartInstanceCantidad.destroy();
      this.chartInstanceCantidad = null;
    }
    if (this.chartInstanceConsumo) {
      this.chartInstanceConsumo.destroy();
      this.chartInstanceConsumo = null;
    }
  },
};
</script>

<style scoped>
.card-section-title.section-title {
  font-weight: 800;
  color: #1f2d5a;
  letter-spacing: 0.6px;
}

.card-section-title.section-title::after {
  content: "";
  display: block;
  width: 44px;
  height: 3px;
  margin-top: 6px;
  border-radius: 999px;
  background: #f6a500;
}

.table.table-informe-extrusion {
  font-size: 0.8rem;
}

/* Líneas muy suaves, casi imperceptibles */
.table-informe-extrusion thead th,
.table-informe-extrusion tbody td {
  border-color: rgba(0, 0, 0, 0.04) !important;
  border-bottom-width: 1px;
}
.table-informe-extrusion tbody tr {
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}
.table-informe-extrusion thead th {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

/* Guiones "—" de celdas vacías muy suaves, que no se noten */
.table-informe-extrusion tbody tr.fila-vacia td {
  color: rgba(0, 0, 0, 0.12);
}

.extrusion-total-text {
  margin-top: 0.5rem;
  display: inline-block;
  padding-bottom: 0.35rem;
  border-bottom: 3px solid #f6a500;
  border-radius: 2px;
}

.extrusion-chart-wrap {
  position: relative;
  height: 180px;
  width: 100%;
}
.extrusion-chart-wrap canvas {
  max-height: 180px;
}

.extrusion-pagination {
  gap: 0.5rem;
}

.extrusion-pagination-btn {
  min-width: 28px;
  height: 26px;
  padding: 0 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1f2d5a;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.extrusion-pagination-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #1f2d5a;
}

.extrusion-pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.extrusion-pagination-text {
  font-size: 0.85rem;
  color: #1f2d5a;
  min-width: 2rem;
  text-align: center;
}
</style>
